const defaultTheme = require('tailwindcss/defaultTheme');
const plugin = require('tailwindcss/plugin')
const postcss = require('postcss');

const withOpacityValue = (variable) => {
  return ({ opacityValue }) => {
    if (opacityValue === undefined) {
      return `rgb(var(${variable}))`;
    }
    return `rgb(var(${variable}) / ${opacityValue})`;
  };
};
const withVar = (variable) => {
  return () => {
    return `var(${variable})`;
  };
};

/** @type {import('tailwindcss').Config} \*/
module.exports = {
  darkMode: 'class',
  content: [
    './app/**/*.{js,ts,jsx,tsx}',
    './src/pages/**/*.{js,ts,jsx,tsx}',
    './src/components/**/*.{js,ts,jsx,tsx}',
    './src/providers/**/*.{js,ts,jsx,tsx}',
    './src/utils/**/*.{js,ts,jsx,tsx}',
    './src/stories/**/*.{js,ts,jsx,tsx}',
  ],
  safelist: [
    'items-start',
    'items-end',
    'items-stretch',
    'items-center',
    'justify-items-start',
    'justify-items-end',
    'justify-items-center',
    'justify-items-stretch',
  ],
  theme: {
    fontSize: {
      size0: ['120px', '128px'],
      size1: ['96px', '104px'],
      size2: ['80px', '88px'],
      size3: ['64px', '72px'],
      size4: ['60px', '64px'],
      size5: ['44px', '48px'],
      size5b: ['40px', '48px'],
      size6: ['40px', '44px'],
      size7: ['32px', '40px'],
      size8: ['32px', '36px'],
      size9: ['24px', '28px'],
      size10: ['20px', '28px'],
      size11: ['20px', '32px'],
      size12: ['18px', '22px'],
      size13: ['16px', '24px'],
      size14: ['12px', '16px'],
      base: ['16px', '24px'],
      size15: ['16px', '20px'],
      size16: ['14px', '20px'],
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      bold: 700,
    },
    extend: {
      borderRadius: {
        DEFAULT: '40px',
        md: '1.25rem',
        lg: '2rem',
      },
      fontFamily: {
        sans: ['var(--font-circular)', ...defaultTheme.fontFamily.sans],
      },
      margin: {
        'gutter-desktop': '70px',
        'gutter-mobile': '24px',
      },
      padding: {
        'gutter-desktop': '70px',
        'gutter-mobile': '24px',
      },
      screens: {
        narrow: '1024px',
        l: '1280px',
        xl: '1440px',
        max: '1580px',
        maxPadded: '1720px',
        flat: {
          raw: '(max-height: 700px) and (orientation: landscape)',
        },
        flatter: {
          raw: '(max-height: 500px) and (orientation: landscape)',
        },
      },
      letterSpacing: {
        tightXXL: '-.08em',
        tightXL: '-.07em',
        tightL: '-.06em',
        tightM: '-.05em',
        tightS: '-.04em',
        tightXS: '-.03em',
        tightXXS: '-.01em',
      },
      gridColumn: {
        'span-13': 'span 13 / span 13',
        'span-14': 'span 14 / span 14',
        'span-15': 'span 15 / span 15',
        'span-16': 'span 16 / span 16',
        'span-17': 'span 17 / span 17',
        'span-18': 'span 18 / span 18',
        'span-19': 'span 19 / span 19',
        'span-23': 'span 23 / span 23',
      },
      gridColumnStart: {
        14: '14',
        15: '15',
        16: '16',
        17: '17',
        18: '18',
        19: '19',
        20: '20',
        21: '21',
        22: '22',
        23: '23',
        24: '24',
      },
      colors: {
        50: withVar('--color-50'),
        grey: {
          100: withVar('--color-grey-100'),
          80: withVar('--color-grey-80'),
          60: withVar('--color-grey-60'),
          40: withVar('--color-grey-40'),
          20: withVar('--color-grey-20'),
        },
        inverse: withVar('--color-inverse'),
        background: withVar('--color-background'),
        buttons: withVar('--color-buttons'),
      },
      spacing: {
        22: '22px',
        30: '30px',
      },
      blur: {
        xss: '2px',
        xs: '3px',
      },
      boxShadow: {
        modal: '0 0 10px rgba(0, 0, 0, 0.25)',
        header: '0px 4px 16px rgba(0, 0, 0, 0.08)',
      },
      visibility: ['group-hover'],
      transitionTimingFunction: {
        'micro-enter': 'cubic-bezier(0.35, 0, 0.5, 1.0)',
        'micro-exit': 'cubic-bezier(0.35, 0, 0.25, 1.0)',
        'layout-enter': 'cubic-bezier(0, 0, 0.1, 1.0)',
        'layout-exit': 'cubic-bezier(0.35, 0, 0.8, 1.0)',
      },
      height: (theme) => ({
        auto: 'auto',
        ...theme('spacing'),
        mFull: '100%',
        mScreen: 'calc(var(--vh) * 100)',
      }),
      minHeight: (theme) => ({
        0: '0',
        ...theme('spacing'),
        mFull: '100%',
        mScreen: 'calc(var(--vh) * 100)',
      }),
    },
  },
  plugins: [
    function ({ addVariant, e }) {
      addVariant('first-child', ({ modifySelectors, separator }) => {
        modifySelectors(({ className }) => {
          return `.${e(`first-child${separator}${className}`)} >*:first-child`;
        });
      });
      addVariant('supports-blur', ({ container, separator }) => {
        const supportsRule = postcss.atRule({
          name: 'supports',
          params: '(backdrop-filter: blur(1px))',
        });
        supportsRule.append(container.nodes);
        container.append(supportsRule);
        supportsRule.walkRules((rule) => {
          rule.selector = `.${e(
            `supports-blur${separator}`,
          )}${rule.selector.slice(1)}`;
        });
      });
    },
    require('@tailwindcss/line-clamp'),
    require('@tailwindcss/typography')({
      target: 'legacy',
    }),
    require('@tailwindcss/aspect-ratio'),
    plugin(function({ matchUtilities }) {
      matchUtilities(
        {
          // following from .grid-standard
          'gridwidth12': (m) => {
            const gap = 32;
            const n = 12;
            const slope = m/n;
            const intercept = m / n * (gap * (n - 1));
            return {
              width: `calc(${m-1} * ${gap}px + ${slope} * 100% - ${intercept}px)`
            }
          },
          // following from .grid-standard
          'gridwidth4': (m) => {
            const gap = 16;
            const n = 4;
            const slope = m / n;
            const intercept = m / n * (gap * (n - 1));
            return {
              width: `calc(${m-1} * ${gap}px + ${slope} * 100% - ${intercept}px)`
            }
          },
        },
      )
    }),
  ],
};
